import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "../supabase";
import Header from "../components/Header";
import Dropdown from "../components/Dropdown";
import Slider from "../components/Slider";
import Toast from "../components/Toast";
import ConfirmModal from "../components/ConfirmModal";
import Footer from "../components/Footer";
import BottomSheet from "../components/BottomSheet";
import { TARGETS } from "../utils/buildPrompt";
import { GUEST_LIMIT, getGuestCount, incrementGuestCount } from "../utils/guestCount";

const IconCopy = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
    <path d="M4.66667 12.0002V1.3335H13.3333V12.0002H4.66667ZM2 14.6668V4.00016H3.33333V13.3335H10.6667V14.6668H2Z" fill="black"/>
  </svg>
);
const IconCopyDone = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
    <mask id="chkm" style={{maskType:'alpha'}} maskUnits="userSpaceOnUse" x="0" y="0" width="24" height="24">
      <rect width="24" height="24" fill="#D9D9D9"/>
    </mask>
    <g mask="url(#chkm)">
      <path d="M9.5501 18L3.8501 12.3L5.2751 10.875L9.5501 15.15L18.7251 5.97498L20.1501 7.39998L9.5501 18Z" fill="#111111"/>
    </g>
  </svg>
);
const IconShare = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
    <path d="M11.3335 14.6666C10.7779 14.6666 10.3057 14.4721 9.91683 14.0833C9.52794 13.6944 9.3335 13.2221 9.3335 12.6666C9.3335 12.5999 9.35016 12.4444 9.3835 12.1999L4.70016 9.46658C4.52238 9.63325 4.31683 9.76392 4.0835 9.85858C3.85016 9.95325 3.60016 10.0004 3.3335 9.99992C2.77794 9.99992 2.30572 9.80547 1.91683 9.41658C1.52794 9.0277 1.3335 8.55547 1.3335 7.99992C1.3335 7.44436 1.52794 6.97214 1.91683 6.58325C2.30572 6.19436 2.77794 5.99992 3.3335 5.99992C3.60016 5.99992 3.85016 6.04725 4.0835 6.14192C4.31683 6.23659 4.52238 6.36703 4.70016 6.53325L9.3835 3.79992C9.36127 3.72214 9.3475 3.64725 9.34216 3.57525C9.33683 3.50325 9.33394 3.42259 9.3335 3.33325C9.3335 2.7777 9.52794 2.30547 9.91683 1.91659C10.3057 1.5277 10.7779 1.33325 11.3335 1.33325C11.8891 1.33325 12.3613 1.5277 12.7502 1.91659C13.1391 2.30547 13.3335 2.7777 13.3335 3.33325C13.3335 3.88881 13.1391 4.36103 12.7502 4.74992C12.3613 5.13881 11.8891 5.33325 11.3335 5.33325C11.0668 5.33325 10.8168 5.28592 10.5835 5.19125C10.3502 5.09659 10.1446 4.96614 9.96683 4.79992L5.2835 7.53325C5.30572 7.61103 5.31972 7.68614 5.3255 7.75858C5.33127 7.83103 5.33394 7.91147 5.3335 7.99992C5.33305 8.08836 5.33038 8.16903 5.3255 8.24192C5.32061 8.31481 5.30661 8.3897 5.2835 8.46658L9.96683 11.1999C10.1446 11.0333 10.3502 10.9028 10.5835 10.8086C10.8168 10.7144 11.0668 10.667 11.3335 10.6666C11.8891 10.6666 12.3613 10.861 12.7502 11.2499C13.1391 11.6388 13.3335 12.111 13.3335 12.6666C13.3335 13.2221 13.1391 13.6944 12.7502 14.0833C12.3613 14.4721 11.8891 14.6666 11.3335 14.6666Z" fill="white"/>
  </svg>
);

const DRAFT_KEY = 'toneplz_draft';
const loadDraft = () => {
  try { return JSON.parse(localStorage.getItem(DRAFT_KEY) || '{}'); } catch { return {}; }
};

export default function MainPage({ user, credits, fetchCredits }) {
  const navigate = useNavigate();
  // 로그인(OAuth 리다이렉트)으로 페이지가 새로고침돼도 입력/선택이 유지되도록 복원
  const [tab, setTab] = useState(() => loadDraft().tab || 'target');
  const [target, setTarget] = useState(() => loadDraft().target || '');
  const [levelIdx, setLevelIdx] = useState(() => loadDraft().levelIdx ?? 0);
  const [concept, setConcept] = useState(() => loadDraft().concept || '');
  const [detail, setDetail] = useState(() => loadDraft().detail || '');
  const [input, setInput] = useState(() => loadDraft().input || '');
  const [msgMode, setMsgMode] = useState(() => loadDraft().msgMode || 'convert'); // convert | reply
  const [intent, setIntent] = useState(() => loadDraft().intent || '');
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [streaming, setStreaming] = useState(false);
  const [copied, setCopied] = useState(false);
  const [toast, setToast] = useState(null);
  const [showConfirm, setShowConfirm] = useState(false);
  const [favorites, setFavorites] = useState([]);
  const [shareId, setShareId] = useState(null);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [sheetTab, setSheetTab] = useState('company');
  const resultRef = useRef(null);

  const isLoggedIn = !!user;
  const detailBytes = new TextEncoder().encode(detail).length;
  const inputBytes = new TextEncoder().encode(input).length;
  const hasMode = tab === 'target' ? !!target : !!concept;
  const canConvert = hasMode && !!input.trim() && inputBytes <= 2000 && !loading && !streaming;

  const currentFavorite = favorites.find(f =>
    f.tab === tab && f.target === target && f.level_idx === levelIdx &&
    f.concept === concept && (f.detail ?? '') === (detail ?? '')
  );
  const isFavorited = !!currentFavorite;

  // 입력/선택을 로컬에 저장해 로그인 왕복 후에도 복원되게 함
  useEffect(() => {
    try {
      localStorage.setItem(DRAFT_KEY, JSON.stringify({ tab, target, levelIdx, concept, detail, input, msgMode, intent }));
    } catch {}
  }, [tab, target, levelIdx, concept, detail, input, msgMode, intent]);

  useEffect(() => {
    if (!user?.id) return;
    const loadFavorites = async () => {
      const { data } = await supabase
        .from('favorites')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });
      if (data) setFavorites(data);
    };
    loadFavorites();
  }, [user]);

  // eslint-disable-next-line no-unused-vars
  const fetchFavorites = async () => {
    if (!user?.id) return;
    const { data } = await supabase
      .from('favorites')
      .select('*')
      .eq('user_id', user.id)
      .order('created_at', { ascending: false });
    if (data) setFavorites(data);
  };

  const handleToggleFavorite = async () => {
    if (!user?.id) {
      setToast('로그인하면 즐겨찾기에 저장할 수 있어요 ★');
      return;
    }
    if (isFavorited) {
      await supabase.from('favorites').delete().eq('id', currentFavorite.id);
      setFavorites(prev => prev.filter(f => f.id !== currentFavorite.id));
      setToast('즐겨찾기에서 삭제됐어요');
    } else {
      if (favorites.length >= 5) {
        setToast('즐겨찾기는 최대 5개까지 가능해요');
        return;
      }
      const { data } = await supabase.from('favorites').insert({
        user_id: user.id,
        tab, target, level_idx: levelIdx, concept, detail,
        label: target ? `${TARGETS[target]?.label}` : concept,
      }).select().single();
      if (data) setFavorites(prev => [data, ...prev]);
      setToast('즐겨찾기에 저장됐어요 ★');
    }
  };

  const handleTabSwitch = (t) => {
    setTab(t); setTarget(''); setConcept(''); setResult(null); setLevelIdx(0); setShareId(null);
  };

  const handleConvert = async () => {
    if (!canConvert) return;
    if (isLoggedIn) {
      // 잔액 정보가 로드된 경우에만 사전 안내. (null이면 서버가 최종 판단)
      if (credits !== null && credits <= 0) {
        setToast('말풍선이 부족해요! 충전이 필요해요.');
        return;
      }
      // A+C 하이브리드: 평소엔 바로 변환, 마지막 1개일 때만 확인창
      if (credits !== null && credits <= 1) {
        setShowConfirm(true);
        return;
      }
      await doConvert();
      return;
    }
    const count = getGuestCount();
    if (count >= GUEST_LIMIT) {
      setToast('오늘 무료 변환을 다 사용했어요. 로그인하면 더 사용할 수 있어요!');
      return;
    }
    incrementGuestCount();
    await doConvert();
  };

  const doConvert = async () => {
    setLoading(true); setResult(null); setShareId(null);
    try {
      const res = await fetch('/api/convert', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          input: input.trim(),
          userId: user?.id ?? null,
          tab, target, levelIdx, concept, detail,
          mode: msgMode,
          intent: msgMode === 'reply' ? intent.trim() : '',
        }),
      });

      if (res.status === 402) {
        setToast('말풍선이 부족해요! 충전이 필요해요.');
        setLoading(false);
        return;
      }

      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        setResult(data.error || '오류가 났어요. 다시 시도해주세요.');
        setLoading(false);
        return;
      }

      // 스트리밍 수신: 네트워크로 받은 텍스트를 버퍼에 쌓고,
      // 타자기처럼 일정한 속도로 한 글자씩 부드럽게 표시
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let received = '';
      let doneReceiving = false;

      const receivePromise = (async () => {
        let firstChunk = true;
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          received += decoder.decode(value, { stream: true });
          if (firstChunk) {
            firstChunk = false;
            setLoading(false);
            setStreaming(true);
          }
        }
      })();

      await new Promise((resolve) => {
        let shown = 0;
        const timer = setInterval(() => {
          if (shown < received.length) {
            // 밀린 양이 많으면 조금씩 빨라져서 자연스럽게 따라잡음
            const backlog = received.length - shown;
            shown = Math.min(shown + Math.max(1, Math.ceil(backlog / 30)), received.length);
            setResult(received.slice(0, shown));
          } else if (doneReceiving) {
            clearInterval(timer);
            resolve();
          }
        }, 35);
        receivePromise.finally(() => { doneReceiving = true; });
      });

      setStreaming(false);
      const text = received.trim();
      setResult(text || '변환 실패');

      if (text) {
        const shareRow = {
          user_id: user?.id ?? null,
          tab, target, level_idx: levelIdx, concept, detail,
          input_text: input.trim(),
          result_text: text,
        };
        // mode 컬럼이 아직 없어도 공유가 실패하지 않도록 폴백
        let { data: shareData, error: shareErr } = await supabase.from('shares')
          .insert({ ...shareRow, mode: msgMode }).select().single();
        if (shareErr) {
          ({ data: shareData } = await supabase.from('shares').insert(shareRow).select().single());
        }
        if (shareData) setShareId(shareData.id);
      }

      if (user?.id) fetchCredits(user.id);
      // 확인창 없이 변환한 경우에도 차감 사실을 가볍게 알림
      if (user?.id && text) {
        setToast(`말풍선 1개 사용 · ${Math.max(0, (credits ?? 1) - 1)}개 남음`);
      }
    } catch {
      setStreaming(false);
      setResult('오류가 났어요. 다시 시도해주세요.');
    }
    setLoading(false);
  };

  const handleConfirmedConvert = async () => {
    setShowConfirm(false);
    await doConvert();
  };

  const handleCopy = async () => {
    if (!result) return;
    try { await navigator.clipboard.writeText(result); } catch {}
    setCopied(true);
    setTimeout(() => setCopied(false), 1800);
  };

  const handleShare = async () => {
  if (!result || !shareId) return;
  const shareUrl = `https://toneplz.vercel.app/share/${shareId}`;
  try {
    if (navigator.share) {
      // url만 전달 — title을 함께 보내면 카카오톡 등에서 링크가 중복 전송됨
      await navigator.share({ url: shareUrl });
    } else {
      await navigator.clipboard.writeText(shareUrl);
      setToast('링크가 복사됐어요!');
    }
  } catch {
    // 사용자가 공유 시트를 닫은 경우 등 — 무시
  }
};

  const guestCount = getGuestCount();

  return (
    <div style={{ minHeight: '100vh', background: '#f0f0f0', display: 'flex', justifyContent: 'center' }}>
      <div style={{ width: '100%', maxWidth: 430, background: '#ffffff', minHeight: '100vh', display: 'flex', flexDirection: 'column', fontFamily: "'DM Mono', monospace" }}>

        <Header isLoggedIn={isLoggedIn} credits={credits} />

        {!isLoggedIn && (
          <div onClick={() => navigate('/login')} style={{ margin: '12px 16px 0', padding: '10px 14px', background: '#f5f5f5', border: '1px solid #e0e0e0', borderRadius: 8, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'space-between', animation: 'fadeInUp .2s ease' }}>
            <span style={{ fontSize: 13, color: '#555555', whiteSpace: 'nowrap' }}>
              오늘 무료 <strong style={{ color: '#111111' }}>{guestCount}/{GUEST_LIMIT}</strong>회 사용
            </span>
            <span style={{ fontSize: 13, color: '#111111', fontWeight: 700, border: '1px solid #111111', borderRadius: 8, padding: '2px 10px', whiteSpace: 'nowrap' }}>로그인 시 3개 무료 →</span>
          </div>
        )}

        <div style={{ padding: '12px 16px', display: 'flex', flexDirection: 'column', gap: 12, flex: 1 }}>

          {/* step1 */}
          <div style={{ background: '#ffffff', borderRadius: 12, overflow: 'hidden', border: '1px solid #111111', position: 'relative', zIndex: 10, flexShrink: 0 }}>
            <div style={{ background: '#111111', color: '#ffffff', padding: '10px 14px', borderRadius: '12px 12px 0 0', fontSize: 16, fontWeight: 700, letterSpacing: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between', height: 40 }}>
              <span>step1.&nbsp;&nbsp;대상 선택</span>
              {(tab === 'target' ? !!target : !!concept) && (
                <button onClick={handleToggleFavorite}
                  style={{ background: isFavorited ? '#FFD700' : 'transparent', color: isFavorited ? '#111111' : '#ffffff', border: `1px solid ${isFavorited ? '#FFD700' : 'rgba(255,255,255,0.55)'}`, borderRadius: 8, cursor: 'pointer', padding: '3px 11px', fontSize: 13, fontWeight: 700, letterSpacing: 0, display: 'flex', alignItems: 'center', gap: 4, alignSelf: 'center', fontFamily: "'DM Mono', monospace", whiteSpace: 'nowrap' }}>
                  {isFavorited ? '★ 저장됨' : '☆ 즐겨찾기'}
                </button>
              )}
            </div>
            <div style={{ padding: 14 }}>
              <div style={{ display: 'flex', gap: 6, marginBottom: 12, background: '#f5f5f5', borderRadius: 12, padding: 3 }}>
                {[['target', '상대방'], ['concept', '컨셉']].map(([t, label]) => (
                  <button key={t} onClick={() => handleTabSwitch(t)} style={{ flex: 1, padding: '6px 0', background: tab === t ? '#111111' : 'transparent', color: tab === t ? '#ffffff' : '#888888', border: 'none', borderRadius: 8, fontSize: 15, fontWeight: tab === t ? 500 : 400, cursor: 'pointer', fontFamily: "'DM Mono', monospace" }}>{label}</button>
                ))}
              </div>

              {tab === 'target' && (
  <div>
    <div style={{ fontSize: 15, color: '#555555', marginBottom: 8, fontWeight: 600 }}>누구에게 말하나요</div>
    <Dropdown
      mode="target"
      value={target}
      onChange={(v, favData) => {
        setTarget(v);
        if (favData) {
          setLevelIdx(favData.level_idx ?? 0);
          setDetail(favData.detail ?? '');
        } else {
          setLevelIdx(0);
        }
        setResult(null);
      }}
      isLoggedIn={isLoggedIn}
      favorites={favorites}
    />
    {target && (
      <>
        <Slider target={target} value={levelIdx} onChange={setLevelIdx} />
        <div style={{ marginTop: 12, animation: 'fadeInUp .2s ease' }}>
          <div style={{ fontSize: 15, color: '#555555', fontWeight: 600, marginBottom: 8 }}>
            디테일 추가 <span style={{ color: '#888888', fontWeight: 400 }}>(선택)</span>
          </div>
          <div style={{ position: 'relative' }}>
            <input value={detail} onChange={e => setDetail(e.target.value)} maxLength={100}
              placeholder="이 사람의 특징을 입력해주세요..."
              style={{ width: '100%', padding: '9px 90px 9px 14px', border: '1px solid #111111', borderRadius: 8, fontSize: 16, color: '#111111', background: '#f5f5f5', fontStyle: 'italic', fontFamily: "'DM Mono', monospace" }}
            />
            <span style={{ position: 'absolute', right: 14, top: '50%', transform: 'translateY(-50%)', fontSize: 13, color: '#888888' }}>
              {detailBytes} / 100byte
            </span>
          </div>
        </div>
      </>
    )}
  </div>
)}

{tab === 'concept' && (
  <div>
    <div style={{ fontSize: 15, color: '#555555', marginBottom: 8, fontWeight: 600 }}>어떤 컨셉으로 바꿀까요</div>
    <Dropdown
      mode="concept"
      value={concept}
      onChange={(v) => { setConcept(v); setResult(null); }}
      isLoggedIn={isLoggedIn}
    />
  </div>
)}

            </div>
          </div>

          {/* step2 */}
          <div style={{ background: '#ffffff', borderRadius: 12, overflow: 'hidden', border: '1px solid #111111', display: 'flex', flexDirection: 'column' }}>
            <div style={{ background: '#111111', color: '#ffffff', padding: '10px 14px', borderRadius: '12px 12px 0 0', fontSize: 16, fontWeight: 700, letterSpacing: 1, display: 'flex', alignItems: 'center', flexShrink: 0 }}>
              <span>step2.&nbsp;&nbsp;{msgMode === 'reply' ? '받은 메시지' : '하고싶은 말'}</span>
            </div>
            <div style={{ padding: 14, display: 'flex', flexDirection: 'column' }}>
              {/* 모드 토글: 내 말 바꾸기 / 답장 만들기 */}
              <div style={{ display: 'flex', gap: 6, marginBottom: 12, background: '#f5f5f5', borderRadius: 12, padding: 3 }}>
                {[['convert', '내 말 바꾸기'], ['reply', '답장 만들기']].map(([m, label]) => (
                  <button key={m} onClick={() => { setMsgMode(m); setResult(null); setShareId(null); }}
                    style={{ flex: 1, padding: '6px 0', background: msgMode === m ? '#111111' : 'transparent', color: msgMode === m ? '#ffffff' : '#888888', border: 'none', borderRadius: 8, fontSize: 15, fontWeight: msgMode === m ? 500 : 400, cursor: 'pointer', fontFamily: "'DM Mono', monospace" }}>
                    {label}
                  </button>
                ))}
              </div>
              <textarea value={input} onChange={e => setInput(e.target.value)}
                placeholder={msgMode === 'reply' ? '상대가 보낸 메시지를 붙여넣어주세요...' : '하고싶은말을 입력해주세요...'}
                style={{ width: '100%', minHeight: msgMode === 'reply' ? 110 : 140, border: 'none', resize: 'none', fontSize: 16, lineHeight: 1.7, color: '#111111', background: 'transparent', padding: 0, fontFamily: "'DM Mono', monospace", outline: 'none' }}
              />
              {msgMode === 'reply' && (
                <div style={{ marginTop: 12, animation: 'fadeInUp .2s ease' }}>
                  <div style={{ fontSize: 15, color: '#555555', fontWeight: 600, marginBottom: 8 }}>
                    어떻게 답할까요? <span style={{ color: '#888888', fontWeight: 400 }}>(선택)</span>
                  </div>
                  <input value={intent} onChange={e => setIntent(e.target.value)} maxLength={100}
                    placeholder="예: 정중하게 거절하고 싶어요"
                    style={{ width: '100%', padding: '9px 14px', border: '1px solid #111111', borderRadius: 8, fontSize: 16, color: '#111111', background: '#f5f5f5', fontStyle: 'italic', fontFamily: "'DM Mono', monospace" }}
                  />
                </div>
              )}
              <div style={{ marginTop: 12, display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 16, flexShrink: 0 }}>
                <span style={{ fontSize: 15, color: inputBytes > 2000 ? '#e53935' : '#bbbbbb' }}>
                  {inputBytes} / 2000byte
                </span>
                <button onClick={handleConvert} disabled={!canConvert}
                  style={{ background: canConvert ? '#111111' : '#888888', color: '#ffffff', border: 'none', borderRadius: 8, fontSize: 15, fontWeight: 700, padding: '11px 20px', cursor: canConvert ? 'pointer' : 'not-allowed', opacity: canConvert ? 1 : 0.5, whiteSpace: 'nowrap', fontFamily: "'DM Mono', monospace", display: 'flex', alignItems: 'center', gap: 8 }}>
                  {(loading || streaming) && <span style={{ width: 14, height: 14, border: '2px solid rgba(255,255,255,.4)', borderTopColor: '#ffffff', borderRadius: '50%', display: 'inline-block', animation: 'spin .7s linear infinite' }} />}
                  {msgMode === 'reply' ? '답장 만들기' : '번역하기'}
                </button>
              </div>
            </div>
          </div>

          {/* step3 */}
          <div ref={resultRef} style={{ background: '#E3E3E3', borderRadius: 12, border: '1px solid #111111', overflow: 'hidden', minHeight: 200, display: 'flex', flexDirection: 'column' }}>
            <div style={{ background: '#111111', color: '#ffffff', padding: '10px 14px', borderRadius: '12px 12px 0 0', fontSize: 16, fontWeight: 700, letterSpacing: 1, display: 'flex', alignItems: 'center', flexShrink: 0 }}>
              <span>step3.&nbsp;&nbsp;변환결과</span>
            </div>
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', padding: 14, gap: 12 }}>
              {loading && (
                <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, fontSize: 15, color: '#777' }}>
                  <span style={{ width: 16, height: 16, border: '2px solid #bbbbbb', borderTopColor: '#111111', borderRadius: '50%', display: 'inline-block', animation: 'spin .7s linear infinite' }} />
                  변환하는 중이에요
                </div>
              )}
              {result && !loading && (
                <>
                  <div style={{ flex: 1 }}>
                    <span style={{ display: 'inline', background: '#ffffff', fontSize: 16, lineHeight: 1.9, color: '#111111', wordBreak: 'keep-all', whiteSpace: 'pre-wrap', boxDecorationBreak: 'clone', WebkitBoxDecorationBreak: 'clone', animation: 'fadeInUp .2s ease', fontFamily: "'DM Mono', monospace" }}>
                      {result}{streaming && '▍'}
                    </span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0, gap: 8 }}>
                    <button onClick={handleConvert} disabled={!canConvert}
                      style={{ padding: '8px 14px', background: '#ffffff', color: canConvert ? '#111111' : '#bbbbbb', border: `1px solid ${canConvert ? '#111111' : '#bbbbbb'}`, borderRadius: 8, fontSize: 15, fontWeight: 700, cursor: canConvert ? 'pointer' : 'not-allowed', display: 'flex', alignItems: 'center', gap: 6, whiteSpace: 'nowrap', fontFamily: "'DM Mono', monospace" }}>
                      ↻ 다시 변환
                    </button>
                    <div style={{ background: '#f0f0f0', border: '1px solid #111111', borderRadius: 8, display: 'flex', alignItems: 'center', padding: 3 }}>
                      <button onClick={handleCopy} style={{ padding: '8px 14px', background: '#ffffff', color: '#111111', border: '1px solid #111111', borderRadius: 8, fontSize: 15, fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6, whiteSpace: 'nowrap', fontFamily: "'DM Mono', monospace" }}>
                        {copied ? <IconCopyDone /> : <IconCopy />} {copied ? '복사됨' : '복사'}
                      </button>
                      <button onClick={handleShare}
                        disabled={!shareId}
                        style={{ padding: '8px 14px', background: shareId ? '#111111' : '#888888', color: '#ffffff', border: 'none', borderRadius: 8, fontSize: 15, fontWeight: 700, cursor: shareId ? 'pointer' : 'not-allowed', display: 'flex', alignItems: 'center', gap: 6, whiteSpace: 'nowrap', marginLeft: 3, fontFamily: "'DM Mono', monospace" }}>
                        <IconShare /> 공유
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>

        </div>

        <Footer onOpenSheet={(tab) => { setSheetTab(tab); setSheetOpen(true); }} />

      </div>

      {showConfirm && (
        <ConfirmModal
          credits={credits}
          onConfirm={handleConfirmedConvert}
          onCancel={() => setShowConfirm(false)}
        />
      )}
      {toast && <Toast message={toast} onClose={() => setToast(null)} />}
      {sheetOpen && (
        <BottomSheet
          sheetTab={sheetTab}
          setSheetTab={setSheetTab}
          onClose={() => setSheetOpen(false)}
        />
      )}
    </div>
  );
}